/**
 * DocumentChangeCard - Card for document (blob) entity changes.
 *
 * Displays file name, collection, and a download link.
 * When a syncVariant is provided, inline Push/Reject or Retry actions
 * appear next to the download button.
 */

import {
  AlertTriangle,
  Check,
  Download,
  Edit3,
  FileText,
  Loader2,
  RotateCcw,
  Upload,
  X,
} from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
import { toast } from 'sonner';

import { Badge } from '@/components/ui/catalyst/badge';
import { useLazyGetBlobDetailQuery } from '@/services/api/blobService';
import type {
  DocumentSyncInfo,
  EntityChangeSchema,
} from '@/services/api/generated/entityChangesService';
import {
  useEntityChangesBlobSinkActionsApproveMutation,
  useEntityChangesBlobSinkActionsRejectMutation,
  useEntityChangesBlobSinkActionsRetryMutation,
  useEntityChangesBlobSinkActionUpdateTargetMutation,
} from '@/services/api/generated/entityChangesService';

import { useChangedFields } from '../utils/useChangedFields';
import { useEntityChangeActions } from './EntityChangeActionsContext';
import { EntityChangeCardShell } from './EntityChangeCardShell';
import { type FieldConfig, KeyFieldsGrid, KeyFieldsGridUnchanged } from './KeyFieldsGrid';
import { ShowMoreSection } from './ShowMoreSection';

const BASE_KEY_FIELDS: FieldConfig[] = [
  { key: 'original_filename', label: 'Original Filename' },
  { key: 'collection_name', label: 'Collection' },
];

interface DocumentChangeCardProps {
  entity: EntityChangeSchema;
  matterId: string;
  onOpenPreview?: (blobId: string, name: string) => void;
  statusBadge?: React.ReactNode;
  syncVariant?: 'needs_review' | 'failed';
}

// ---------------------------------------------------------------------------
// Inline sync actions (Push/Reject/Retry) rendered next to the download button
// ---------------------------------------------------------------------------

const InlineSyncActions: React.FC<{
  syncInfo: DocumentSyncInfo;
  variant: 'needs_review' | 'failed';
}> = ({ syncInfo, variant }) => {
  const [approveActions] = useEntityChangesBlobSinkActionsApproveMutation();
  const [rejectActions] = useEntityChangesBlobSinkActionsRejectMutation();
  const [retryActions] = useEntityChangesBlobSinkActionsRetryMutation();
  const [isLoading, setIsLoading] = useState(false);

  const handleApprove = useCallback(async () => {
    setIsLoading(true);
    try {
      await approveActions({
        bulkBlobSinkActionRequestSchema: { action_ids: [syncInfo.action_id] },
      }).unwrap();
      toast.success('Document approved for push');
    } catch {
      toast.error('Failed to approve');
    } finally {
      setIsLoading(false);
    }
  }, [syncInfo.action_id, approveActions]);

  const handleReject = useCallback(async () => {
    setIsLoading(true);
    try {
      await rejectActions({
        bulkBlobSinkActionRejectRequestSchema: { action_ids: [syncInfo.action_id] },
      }).unwrap();
      toast.success('Push rejected');
    } catch {
      toast.error('Failed to reject');
    } finally {
      setIsLoading(false);
    }
  }, [syncInfo.action_id, rejectActions]);

  const handleRetry = useCallback(async () => {
    setIsLoading(true);
    try {
      await retryActions({
        bulkBlobSinkActionRequestSchema: { action_ids: [syncInfo.action_id] },
      }).unwrap();
      toast.success('Retry initiated');
    } catch {
      toast.error('Failed to retry');
    } finally {
      setIsLoading(false);
    }
  }, [syncInfo.action_id, retryActions]);

  if (variant === 'needs_review') {
    return (
      <>
        <button
          type="button"
          onClick={handleApprove}
          disabled={isLoading}
          className="inline-flex items-center gap-1 rounded bg-green-600 px-2 py-0.5 text-xs font-medium text-white hover:bg-green-700 disabled:opacity-50"
        >
          {isLoading ? (
            <Loader2 className="h-3 w-3 animate-spin" />
          ) : (
            <Upload className="h-3 w-3" />
          )}
          Push
        </button>
        <button
          type="button"
          onClick={handleReject}
          disabled={isLoading}
          className="inline-flex items-center gap-1 rounded border border-gray-200 px-2 py-0.5 text-xs font-medium text-gray-500 hover:bg-gray-100 disabled:opacity-50"
        >
          <X className="h-3 w-3" />
          Reject
        </button>
      </>
    );
  }

  return (
    <button
      type="button"
      onClick={handleRetry}
      disabled={isLoading}
      className="inline-flex items-center gap-1 rounded border border-gray-200 px-2 py-0.5 text-xs font-medium text-gray-600 hover:bg-gray-100 disabled:opacity-50"
    >
      {isLoading ? (
        <Loader2 className="h-3 w-3 animate-spin" />
      ) : (
        <RotateCcw className="h-3 w-3" />
      )}
      Retry
    </button>
  );
};

// ---------------------------------------------------------------------------
// Inline CMS target editor (for editing path/name on AWAITING_APPROVAL)
// ---------------------------------------------------------------------------

const CmsTargetField: React.FC<{
  syncInfo: DocumentSyncInfo;
  editable: boolean;
}> = ({ syncInfo, editable }) => {
  const [updateTarget] = useEntityChangesBlobSinkActionUpdateTargetMutation();
  const [isEditing, setIsEditing] = useState(false);
  const [editPath, setEditPath] = useState(syncInfo.target_path ?? '');
  const [editName, setEditName] = useState(syncInfo.target_name ?? '');

  const handleSaveEdit = useCallback(async () => {
    try {
      await updateTarget({
        actionId: syncInfo.action_id,
        updateBlobSinkActionTargetSchema: {
          target_path: editPath || null,
          target_name: editName || null,
        },
      }).unwrap();
      setIsEditing(false);
      toast.success('Target updated');
    } catch {
      toast.error('Failed to update target');
    }
  }, [syncInfo.action_id, editPath, editName, updateTarget]);

  const targetDisplay = [syncInfo.target_path, syncInfo.target_name]
    .filter(Boolean)
    .join('/');

  if (isEditing) {
    return (
      <div className="flex items-center gap-1.5 text-xs text-gray-500">
        <span className="shrink-0 text-gray-400">
          {syncInfo.target_system} &rarr;
        </span>
        <input
          type="text"
          value={editPath}
          onChange={(e) => setEditPath(e.target.value)}
          placeholder="Path"
          className="rounded border border-gray-300 px-1.5 py-0.5 text-xs w-32 focus:border-blue-400 focus:ring-1 focus:ring-blue-400 focus:outline-none"
        />
        <span className="text-gray-300">/</span>
        <input
          type="text"
          value={editName}
          onChange={(e) => setEditName(e.target.value)}
          placeholder="Filename"
          className="rounded border border-gray-300 px-1.5 py-0.5 text-xs w-40 focus:border-blue-400 focus:ring-1 focus:ring-blue-400 focus:outline-none"
        />
        <button
          type="button"
          onClick={handleSaveEdit}
          className="rounded p-0.5 text-green-600 hover:bg-green-50"
        >
          <Check className="h-3.5 w-3.5" />
        </button>
        <button
          type="button"
          onClick={() => setIsEditing(false)}
          className="rounded p-0.5 text-gray-400 hover:bg-gray-100"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
    );
  }

  return (
    <span className="flex items-center gap-1 text-xs text-gray-500 truncate">
      <span className="shrink-0 text-gray-400">
        {syncInfo.target_system} &rarr;
      </span>
      <span className="truncate font-medium text-gray-700">
        {targetDisplay || 'No target set'}
      </span>
      {editable && (
        <button
          type="button"
          onClick={() => {
            setEditPath(syncInfo.target_path ?? '');
            setEditName(syncInfo.target_name ?? '');
            setIsEditing(true);
          }}
          className="shrink-0 text-gray-400 hover:text-gray-600"
          title="Edit target"
        >
          <Edit3 className="h-3 w-3" />
        </button>
      )}
    </span>
  );
};

// ---------------------------------------------------------------------------
// Main card component
// ---------------------------------------------------------------------------

export const DocumentChangeCard: React.FC<DocumentChangeCardProps> = ({
  entity,
  matterId,
  onOpenPreview,
  statusBadge,
  syncVariant,
}) => {
  const changes = useChangedFields(
    entity.change_type,
    entity.changed_fields ?? [],
  );
  const actions = useEntityChangeActions();
  const effectiveStatus =
    actions?.statusOverrides[entity.id as string] ?? entity.status;

  const snapshot =
    entity.change_type === 'deleted' ? entity.old_snapshot : entity.new_snapshot;
  const data = (snapshot?.data ?? {}) as Record<string, unknown>;
  const name = (data.name as string) || 'Unnamed Document';
  const ext = data.file_extension as string | undefined;
  const blobId = data.id as string | undefined;
  const syncInfo = entity.document_sync_info;

  const [triggerGetBlobDetail, { isFetching: isDownloading }] = useLazyGetBlobDetailQuery();
  const [downloadError, setDownloadError] = useState(false);

  const handleDownload = useCallback(async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!blobId || !matterId) return;
    setDownloadError(false);
    try {
      const result = await triggerGetBlobDetail({ matterId, blobId }).unwrap();
      if (result.download_url) {
        window.open(result.download_url, '_blank', 'noopener,noreferrer');
      }
    } catch {
      setDownloadError(true);
    }
  }, [blobId, matterId, triggerGetBlobDetail]);

  // Build key fields: base fields + CMS folder/filename when sync info exists
  const keyFields = useMemo<FieldConfig[]>(() => {
    if (!syncInfo) return BASE_KEY_FIELDS;
    return [
      ...BASE_KEY_FIELDS,
      { key: '_cms_folder', label: 'CMS Folder' },
      { key: '_cms_filename', label: 'CMS Filename' },
    ];
  }, [syncInfo]);

  // Build display data: snapshot data + injected CMS fields
  const displayData = useMemo(() => {
    const base = { ...data };
    if (syncInfo) {
      base._cms_folder = syncInfo.target_path || 'No folder set';
      base._cms_filename = syncInfo.target_name || 'No filename set';
    }
    return base;
  }, [data, syncInfo]);

  return (
    <EntityChangeCardShell
      changeType={entity.change_type}
      name={name}
      icon={FileText}
      iconBgClassName="bg-sky-50"
      iconTextClassName="text-sky-600"
      entityStatus={effectiveStatus}
      onStatusChange={
        actions && syncVariant !== 'needs_review'
          ? (newStatus) => actions.onStatusChange(entity.id as string, newStatus)
          : undefined
      }
      onNameClick={
        blobId && onOpenPreview
          ? () => onOpenPreview(blobId, name)
          : undefined
      }
      badges={
        ext ? (
          <Badge color="zinc" className="text-xs">
            {ext}
          </Badge>
        ) : undefined
      }
      statusBadge={statusBadge}
      changeCount={changes.totalChanges}
      hasExpandedContent
    >
      <KeyFieldsGrid
        fields={keyFields}
        data={displayData}
        changes={changes}
        changeType={entity.change_type}
      />

      {/* Editable CMS target (replaces the static _cms_location field for needs_review) */}
      {syncInfo && syncVariant === 'needs_review' && (
        <div className="mt-1">
          <CmsTargetField syncInfo={syncInfo} editable />
        </div>
      )}

      {/* Error message for failed actions */}
      {syncInfo && syncVariant === 'failed' && syncInfo.error_message && (
        <div className="mt-1 flex items-center gap-1 text-xs text-red-500">
          <AlertTriangle className="h-3 w-3 shrink-0" />
          <span className="truncate">{syncInfo.error_message}</span>
        </div>
      )}

      <ShowMoreSection changeType={entity.change_type}>
        <KeyFieldsGridUnchanged
          fields={keyFields}
          data={displayData}
          changes={changes}
          changeType={entity.change_type}
        />
      </ShowMoreSection>

      {/* Footer: Download + sync actions */}
      <div className="mt-1 flex items-center gap-3">
        {blobId && (
          <>
            <button
              type="button"
              onClick={handleDownload}
              disabled={isDownloading}
              className="inline-flex items-center gap-1.5 text-xs font-medium text-blue-600 hover:text-blue-800 transition-colors disabled:opacity-50"
            >
              {isDownloading ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Download className="h-3.5 w-3.5" />
              )}
              Download file
            </button>
            {downloadError && (
              <span className="text-xs text-red-500">Download failed</span>
            )}
          </>
        )}
        {syncVariant && syncInfo && (
          <>
            {blobId && (
              <span className="text-gray-200">|</span>
            )}
            <InlineSyncActions syncInfo={syncInfo} variant={syncVariant} />
          </>
        )}
      </div>
    </EntityChangeCardShell>
  );
};
